import {
  type DocumentViewerKind,
  documentViewerKind,
} from "@volca/ui/components/ui/document-viewer";
import type { DocumentCitation } from "@/profitability/documents/citation";
import { isMissing, isNumericType } from "@/profitability/missing";
import type {
  ReportColumn,
  ReportDataset,
  ReportValue,
} from "@/profitability/types";
import type {
  CitationPlace,
  CitationsAnswer,
  FigurePart,
  FilePart,
  RunFigure,
} from "./types";

export const isSourced = (dataset: ReportDataset, column: ReportColumn) =>
  !!column.source || !!dataset.traced?.includes(column.key);

export const sourcedColumns = (dataset: ReportDataset) =>
  dataset.columns.filter((column) => isSourced(dataset, column));

export const canShowSource = (
  dataset: ReportDataset,
  column?: ReportColumn | null,
) =>
  column
    ? isSourced(dataset, column)
    : !!dataset.cited || sourcedColumns(dataset).length > 0;

/**
 * The row a reader finds the figure on, as the viewer that opens the file
 * numbers it: its row in the sheet where the API says (`sheet_row`); for a
 * spreadsheet that does not say, the row under a header on row 1 (as the
 * viewer opens it); else the row's number in the table as read.
 */
export const citationRow = (citation: CitationPlace) => {
  if (typeof citation.sheet_row === "number") return citation.sheet_row;
  if (citation.row === null) return null;
  const kind = documentViewerKind({
    contentType: citation.content_type,
    fileName: citation.file_name,
  });
  return kind === "xlsx" || kind === "csv" ? citation.row + 1 : citation.row;
};

/**
 * "Row 13, Labor Cost", "Row 13", "Page 2"; a row read off a page says its
 * page too ("Page 2, row 4, Amount"), since each page's table numbers its
 * rows from 1. `withPage: false` leaves it out where the page is plain (a
 * box drawn on it). Null when nothing places it.
 */
export const citationPlace = (citation: CitationPlace, withPage = true) => {
  if (citation.row === null)
    return citation.page ? `Page ${citation.page}` : null;
  const row = citationRow(citation);
  const place = citation.column
    ? `Row ${row}, ${citation.column}`
    : `Row ${row}`;
  return withPage && citation.page
    ? `Page ${citation.page}, ${place[0].toLowerCase()}${place.slice(1)}`
    : place;
};

/**
 * The citation as the Documents tab and its viewers read it, for the
 * viewer that draws the file (`kind`; null for a table shown as read):
 *
 * - PDF: its page and the row's box (else the table's); with neither box,
 *   the value searched.
 * - Spreadsheet (a workbook, or a CSV drawn as one): the sheet (a CSV has
 *   one) and the cell, on the row the API says it sits on in the sheet
 *   (`sheet_row`). Without that, the preview's row number: the first row
 *   under the header is 1, and the sheet has the header on row 1 above it.
 * - Preview (text, or a CSV too large to draw): the table's index, the
 *   row's number, the column.
 */
export const documentCitation = (
  citation: CitationPlace,
  kind: DocumentViewerKind | null,
): DocumentCitation => {
  const { row, page, sheet, column, column_index: index, value } = citation;
  if (kind === "pdf" || kind === "image" || kind === "docx") {
    const box = citation.row_box ?? citation.box ?? undefined;
    return {
      page: page ?? undefined,
      box,
      // Searched only where no box says where it is: the value as read
      // (412.5) is not always the page's text ($412.50), and a match half
      // lit inside its box reads as a different place.
      find:
        kind === "pdf" &&
        !box &&
        !isMissing(value) &&
        typeof value !== "boolean"
          ? String(value)
          : undefined,
    };
  }
  if (kind === "xlsx" || kind === "csv") {
    const sheetRow = citation.sheet_row ?? (row === null ? null : row + 1);
    return {
      sheet:
        kind === "csv"
          ? undefined
          : (citation.sheet_name ??
            (sheet === null ? undefined : String(sheet))),
      row: sheetRow ?? undefined,
      // A row cited without its cell outlines the row from its first cell.
      column:
        index !== null
          ? String(index + 1)
          : sheetRow === null
            ? undefined
            : "1",
    };
  }
  return {
    sheet: sheet === null ? undefined : String(sheet),
    row: row ?? undefined,
    column: column ?? (index === null ? undefined : String(index + 1)),
  };
};

/** "$1,240.50" → 1240.5, "(12.00)" → -12; a value that is not a number → null. */
export const parseFigure = (value: ReportValue) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const number = Number(cleaned);
  return Number.isFinite(number)
    ? value.trim().startsWith("(") && value.trim().endsWith(")")
      ? -number
      : number
    : null;
};

/** Whether the report's figure is the file's cell, to the cent. */
export const sameValue = (
  report: ReportValue | undefined,
  file: ReportValue,
  column: Pick<ReportColumn, "type">,
) => {
  if (isMissing(report ?? null) || isMissing(file))
    return isMissing(report ?? null) && isMissing(file);
  if (isNumericType(column.type)) {
    const a = parseFigure(report ?? null);
    const b = parseFigure(file);
    return a !== null && b !== null && Math.abs(a - b) < 0.005;
  }
  return (
    String(report).trim().toLowerCase() === String(file).trim().toLowerCase()
  );
};

export const isFilePart = (part: FigurePart) =>
  !!part.input && part.figure === null && part.setting === null;

export const inFile = (part: FigurePart): part is FilePart =>
  isFilePart(part) && !!part.document_id && !!part.file_name;

export const figureParts = (answer: CitationsAnswer | undefined) =>
  answer?.figure?.parts ??
  (answer?.citations ?? [])
    .filter((citation) => citation.row !== null || citation.page !== null)
    .map(
      (citation): FigurePart => ({
        ...citation,
        used: true,
        note: null,
        share: null,
        figure: null,
        setting: null,
      }),
    );

export interface FigureFile {
  document_id: string;
  file_name: string;
  content_type: string | null;
  input: string;
  input_title: string | null;
}

export const figureFiles = (parts: FigurePart[]): FigureFile[] => {
  const files = new Map<string, FigureFile>();
  for (const part of parts) {
    if (!inFile(part) || files.has(part.document_id)) continue;
    files.set(part.document_id, {
      document_id: part.document_id,
      file_name: part.file_name,
      content_type: part.content_type,
      input: part.input,
      input_title: part.input_title,
    });
  }
  return [...files.values()];
};

const BLANK = /(\d+) of (\d+) blank/;

/**
 * How many of a figure's lines were blank in the file, of how many: from
 * the script's note ("1 of 3 blank"), else counted from its listed parts.
 * Null when none were.
 */
export const blankLines = (figure: RunFigure | null | undefined) => {
  if (!figure) return null;
  const noted = BLANK.exec(figure.note ?? "");
  if (noted) return { blank: Number(noted[1]), total: Number(noted[2]) };
  if (figure.how !== "sum" || figure.count > figure.parts.length) return null;
  const counted = figure.parts.filter((part) => part.used && isFilePart(part));
  const blank = counted.filter((part) => isMissing(part.value)).length;
  return blank ? { blank, total: counted.length } : null;
};

/**
 * Whether the report's figure disagrees with the file: a cell that is not
 * the file's cell, or a sum whose counted lines (all listed) do not add up
 * to it. Every other way of making a figure says nothing either way.
 */
export const figureDiffers = (
  figure: RunFigure | null | undefined,
  report: ReportValue | undefined,
  column: Pick<ReportColumn, "type"> | null,
) => {
  if (!figure || !column) return false;
  if (figure.how === "cell") {
    const cell = figure.parts.find(isFilePart);
    return !!cell && !sameValue(report, cell.value, column);
  }
  if (figure.how !== "sum" || figure.count > figure.parts.length) return false;
  if (!isNumericType(column.type) || isMissing(report ?? null)) return false;
  const counted = figure.parts.filter(
    (part) => part.used && !isMissing(part.value),
  );
  if (!counted.length) return false;
  const total = counted.reduce(
    (sum, part) => sum + (parseFigure(part.value) ?? 0),
    0,
  );
  return !sameValue(report, total, column);
};
